import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  MapPin,
  Home,
  Calendar,
  Building,
  Car,
  Dumbbell,
  Trees,
  Shield,
  Zap,
  Waves,
  Users,
  Wifi,
  CloudRain,
  ArrowLeft,
  Phone,
  Mail,
  Check,
  Share2,
  Loader2,
} from 'lucide-react';
import { projectsApi } from '../services/api';
import { companyInfo } from '../data/siteData';
import { Gallery } from '../components/common/Gallery';
import { EnquiryForm } from '../components/common/EnquiryForm';
import { ProjectCard } from '../components/common/ProjectCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const amenityIcons = {
  'Swimming Pool': Waves,
  Gym: Dumbbell,
  Clubhouse: Users,
  '24/7 Security': Shield,
  'Power Backup': Zap,
  'Landscaped Gardens': Trees,
  "Children's Play Area": Users,
  'Covered Parking': Car,
  'Central AC': Building,
  'High-Speed Elevators': Building,
  'Multi-level Parking': Car,
  'Fire Safety Systems': Shield,
  'Conference Rooms': Building,
  Cafeteria: Building,
  ATM: Building,
  'Private Garden': Trees,
  'Tennis Court': Users,
  'Jogging Track': Users,
  'EV Charging': Zap,
  'Smart Home Automation': Wifi,
  'Rainwater Harvesting': CloudRain,
  'Community Hall': Users,
  'Fiber Optic Connectivity': Wifi,
  'Food Court': Building,
  'Conference Center': Building,
  'Green Spaces': Trees,
  Helipad: Building,
};

export default function ProjectDetailPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    // Try fetching by numeric ID first, then by slug search
    const fetchProject = async () => {
      try {
        // Try numeric ID
        if (!isNaN(id)) {
          const p = await projectsApi.getById(id);
          setProject(p);
        } else {
          // It's a slug — search all projects
          const res = await projectsApi.getAll();
          const projects = res.data || res || [];
          setAllProjects(projects);
          const found = projects.find(p => p.slug === id || String(p.id) === id);
          if (found) setProject(found);
          else setNotFound(true);
        }
        // Also fetch all for related projects
        if (!isNaN(id)) {
          const res = await projectsApi.getAll();
          setAllProjects(res.data || res || []);
        }
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  // Helper: get field supporting both snake_case (API) and camelCase (static)
  const f = (p, camel, snake) => p?.[snake] ?? p?.[camel];

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#47ace2] animate-spin" />
      </div>
    );
  }

  if (notFound || !project) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-slate-900 font-outfit mb-4">
            Project Not Found
          </h1>
          <Link to="/projects">
            <Button className="bg-[#47ace2] hover:bg-[#1f7db3] rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Projects
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const statusColors = {
    Ongoing: 'bg-[#47ace2] text-white',
    Completed: 'bg-emerald-500 text-white',
    Upcoming: 'bg-amber-500 text-white',
  };

  const relatedProjects = allProjects
    .filter((p) => p.id !== project.id && p.category === project.category)
    .slice(0, 3);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  // Normalized field accessors
  const priceRange = f(project, 'priceRange', 'price_range');
  const totalUnits = f(project, 'totalUnits', 'total_units');
  const floors = project.floors;
  const completionDate = f(project, 'completionDate', 'completion_date');
  const reraNumber = f(project, 'reraNumber', 'rera_number');
  const highlights = project.highlights || [];
  const amenities = project.amenities || [];
  const configurations = project.configurations || [];
  const images = project.images || [];

  return (
    <div data-testid="project-detail-page" className="pt-20">
      {/* Breadcrumb */}
      <div className="bg-[#F8F9FA] py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <nav className="flex items-center gap-2 text-sm font-manrope">
            <Link to="/" className="text-slate-500 hover:text-[#47ace2]">
              Home
            </Link>
            <span className="text-slate-400">/</span>
            <Link to="/projects" className="text-slate-500 hover:text-[#47ace2]">
              Projects
            </Link>
            <span className="text-slate-400">/</span>
            <span className="text-slate-900">{project.name}</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Badge className={statusColors[project.status]}>{project.status}</Badge>
                <Badge variant="outline">{project.category}</Badge>
                {project.featured && (
                  <Badge className="bg-amber-100 text-amber-700">Featured</Badge>
                )}
              </div>
              <h1
                data-testid="project-name"
                className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 font-outfit"
              >
                {project.name}
              </h1>
              <div className="flex items-center gap-2 text-slate-600 mt-4 font-manrope">
                <MapPin className="w-5 h-5 text-[#47ace2]" />
                {project.location}
              </div>
            </div>
            <div className="flex flex-col items-end gap-4">
              <p className="text-2xl md:text-3xl font-semibold text-[#47ace2] font-outfit">
                {priceRange}
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleShare}
                  data-testid="share-btn"
                  className="rounded-full"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                <a href={`tel:${companyInfo.primaryPhone || '+918766551928'}`}>
                  <Button
                    data-testid="call-btn"
                    className="bg-[#47ace2] hover:bg-[#1f7db3] rounded-full"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call Now
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Gallery images={images} title={project.name} />
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="w-full flex items-center justify-start mb-8 bg-transparent border-b border-slate-200 rounded-none p-0 h-auto overflow-x-auto whitespace-nowrap scrollbar-none flex-nowrap">
                  <TabsTrigger
                    value="overview"
                    data-testid="tab-overview"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#47ace2] data-[state=active]:bg-transparent px-6 py-3"
                  >
                    Overview
                  </TabsTrigger>
                  <TabsTrigger
                    value="amenities"
                    data-testid="tab-amenities"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#47ace2] data-[state=active]:bg-transparent px-6 py-3"
                  >
                    Amenities
                  </TabsTrigger>
                  <TabsTrigger
                    value="floorplans"
                    data-testid="tab-floorplans"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#47ace2] data-[state=active]:bg-transparent px-6 py-3"
                  >
                    Floor Plans
                  </TabsTrigger>
                  <TabsTrigger
                    value="location"
                    data-testid="tab-location"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#47ace2] data-[state=active]:bg-transparent px-6 py-3"
                  >
                    Location
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-0">
                  <div className="prose max-w-none">
                    <h3 className="text-2xl font-semibold text-slate-900 font-outfit mb-4">
                      About {project.name}
                    </h3>
                    <p className="text-slate-600 font-manrope leading-relaxed mb-8">
                      {project.description}
                    </p>

                    {/* Highlights */}
                    {highlights.length > 0 && (
                      <div className="mb-8">
                        <h4 className="text-lg font-semibold text-slate-900 font-outfit mb-4">
                          Project Highlights
                        </h4>
                        <div className="flex flex-wrap gap-3">
                          {highlights.map((highlight, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2 px-4 py-2 bg-[#F0F8FF] rounded-full"
                            >
                              <Check className="w-4 h-4 text-[#47ace2]" />
                              <span className="text-slate-700 font-manrope text-sm">
                                {highlight}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Key Details */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-6 bg-[#F8F9FA] rounded-2xl">
                      <div>
                        <span className="text-slate-500 text-sm font-manrope">Type</span>
                        <p className="text-slate-900 font-semibold font-outfit flex items-center gap-2 mt-1">
                          <Home className="w-4 h-4 text-[#47ace2]" />
                          {project.type}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm font-manrope">Total Units</span>
                        <p className="text-slate-900 font-semibold font-outfit flex items-center gap-2 mt-1">
                          <Building className="w-4 h-4 text-[#47ace2]" />
                          {totalUnits}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm font-manrope">Floors</span>
                        <p className="text-slate-900 font-semibold font-outfit flex items-center gap-2 mt-1">
                          <Building className="w-4 h-4 text-[#47ace2]" />
                          {floors}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm font-manrope">Completion</span>
                        <p className="text-slate-900 font-semibold font-outfit flex items-center gap-2 mt-1">
                          <Calendar className="w-4 h-4 text-[#47ace2]" />
                          {completionDate}
                        </p>
                      </div>
                    </div>

                    {/* Configurations */}
                    <div className="mt-8">
                      <h4 className="text-lg font-semibold text-slate-900 font-outfit mb-4">
                        Available Configurations
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {configurations.map((config, index) => (
                          <div
                            key={index}
                            className="p-4 border border-slate-200 rounded-xl flex items-center gap-3"
                          >
                            <div className="w-10 h-10 bg-[#F0F8FF] rounded-full flex items-center justify-center">
                              <Home className="w-5 h-5 text-[#47ace2]" />
                            </div>
                            <span className="text-slate-700 font-manrope">{config}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* RERA */}
                    {reraNumber && (
                    <div className="mt-8 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                      <p className="text-emerald-800 font-manrope text-sm">
                        <strong>RERA Number:</strong> {reraNumber}
                      </p>
                    </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="amenities" className="mt-0">
                  <h3 className="text-2xl font-semibold text-slate-900 font-outfit mb-6">
                    Amenities & Features
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {amenities.map((amenity, index) => {
                      const Icon = amenityIcons[amenity] || Building;
                      return (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-4 bg-white border border-slate-100 rounded-xl hover:shadow-md transition-shadow"
                        >
                          <div className="w-10 h-10 bg-[#F0F8FF] rounded-full flex items-center justify-center">
                            <Icon className="w-5 h-5 text-[#47ace2]" />
                          </div>
                          <span className="text-slate-700 font-manrope">{amenity}</span>
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="floorplans" className="mt-0">
                  <h3 className="text-2xl font-semibold text-slate-900 font-outfit mb-6">
                    Floor Plans
                  </h3>
                  {project.floorPlans && project.floorPlans.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {project.floorPlans.map((plan, index) => (
                        <div
                          key={index}
                          className="border border-slate-200 rounded-xl overflow-hidden"
                        >
                          <img
                            src={plan}
                            alt={`Floor Plan ${index + 1}`}
                            className="w-full h-auto"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 font-manrope">
                      Floor plans will be available soon.
                    </p>
                  )}
                </TabsContent>

                <TabsContent value="location" className="mt-0">
                  <h3 className="text-2xl font-semibold text-slate-900 font-outfit mb-6">
                    Location
                  </h3>
                  <div className="rounded-xl overflow-hidden border border-slate-200">
                    <div className="aspect-[16/9] bg-slate-100 flex items-center justify-center">
                      <div className="text-center p-8">
                        <MapPin className="w-12 h-12 text-[#47ace2] mx-auto mb-4" />
                        <p className="text-slate-600 font-manrope mb-2">
                          {project.location}
                        </p>
                        <p className="text-slate-500 text-sm font-manrope">
                          Map view available on site visit
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28">
                <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
                  <h3 className="text-xl font-semibold text-slate-900 font-outfit mb-6">
                    Interested in {project.name}?
                  </h3>
                  <EnquiryForm projectName={project.name} />
                </div>

                {/* Contact Card */}
                <div className="mt-6 bg-[#F0F8FF] rounded-2xl p-6">
                  <h4 className="font-semibold text-slate-900 font-outfit mb-4">
                    Need Help?
                  </h4>
                  <div className="space-y-3">
                    <div className="flex flex-col gap-2">
                      <a
                        href={`tel:${companyInfo.primaryPhone || '+918766551928'}`}
                        className="flex items-center gap-3 text-slate-600 hover:text-[#47ace2] transition-colors font-manrope font-semibold"
                      >
                        <Phone className="w-5 h-5 text-[#47ace2]" />
                        +91 8766 551 928 (Primary)
                      </a>
                      <a
                        href={`tel:${companyInfo.secondaryPhone || '07124102028'}`}
                        className="flex items-center gap-3 text-slate-500 hover:text-[#47ace2] transition-colors font-manrope text-sm pl-8"
                      >
                        0712-4102028 (Landline)
                      </a>
                    </div>
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="flex items-center gap-3 text-slate-600 hover:text-[#47ace2] transition-colors font-manrope"
                    >
                      <Mail className="w-5 h-5 text-[#47ace2]" />
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-16 md:py-24 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-3xl font-light tracking-tight text-slate-900 font-outfit mb-12">
              Similar Projects
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedProjects.map((project) => (
                <ProjectCard key={project.id} project={project} variant="compact" />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
